import React, { useState } from 'react'
import { Card, IconButton } from '../ui'
import { formatTimeHorizon } from './timeHorizon.js'
import { KpiFacet } from './facets/KpiFacet.jsx'
import { DeliverablesFacet } from './facets/DeliverablesFacet.jsx'
import { CriteriaFacet } from './facets/CriteriaFacet.jsx'
import { DeadlineFacet } from './facets/DeadlineFacet.jsx'
import { MilestonesFacet } from './facets/MilestonesFacet.jsx'
import { TrackingSection } from './facets/TrackingSection.jsx'

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started', color: '#999' },
  { value: 'on_track', label: 'On track', color: '#3a8a5a' },
  { value: 'at_risk', label: 'At risk', color: '#e89a5d' },
  { value: 'blocked', label: 'Blocked', color: '#c45a5a' },
  { value: 'done', label: 'Done', color: '#3a8a5a' },
]

const FACET_LABELS = {
  kpi: 'KPI',
  deliverables: 'Deliverables',
  criteria: 'Criteria',
  deadline: 'Deadline',
  milestones: 'Milestones',
}

export function SubgoalCard({
  subgoal,
  editable = false,
  onSetStatus,
  onUpdateKpi,
  onToggleDeliverable,
  onAddDeliverable,
  onRemoveDeliverable,
  onToggleMilestone,
  onAddMilestone,
  onRemoveMilestone,
  onUpdateCriteria,
  onUpdateDeadline,
  onUpdateNotes,
  onUpdateTitle,
  onDelete,
  onClick,
}) {
  const [statusOpen, setStatusOpen] = useState(false)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesDraft, setNotesDraft] = useState('')

  const facets = typeof subgoal.facets === 'string' ? JSON.parse(subgoal.facets) : (subgoal.facets || {})
  const tracking = typeof subgoal.tracking === 'string' ? JSON.parse(subgoal.tracking) : (subgoal.tracking || [])
  const currentStatus = STATUS_OPTIONS.find((s) => s.value === subgoal.status) || STATUS_OPTIONS[0]
  const isDone = subgoal.status === 'done'

  function startTitleEdit() {
    if (!editable || !onUpdateTitle) return
    setTitleDraft(subgoal.title)
    setEditingTitle(true)
  }

  function commitTitle() {
    const t = titleDraft.trim()
    if (t && t !== subgoal.title) onUpdateTitle?.(t)
    setEditingTitle(false)
  }

  function startNotesEdit() {
    if (!editable || !onUpdateNotes) return
    setNotesDraft(subgoal.notes || '')
    setEditingNotes(true)
  }

  function commitNotes() {
    const n = notesDraft.trim()
    if (n !== (subgoal.notes || '')) onUpdateNotes?.(n)
    setEditingNotes(false)
  }

  return (
    <Card
      onClick={onClick}
      style={{
        // SubgoalCard uses --bg (slightly darker than --surface) to nest
        // visually inside its parent goal card. Override the default
        // surface tone here.
        background: 'var(--bg)',
        opacity: isDone ? 0.6 : 1,
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={styles.header}>
        <span style={{ ...styles.statusDot, background: currentStatus.color }} />

        {editingTitle ? (
          <input
            style={styles.titleInput}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => e.key === 'Enter' && commitTitle()}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            style={{ ...styles.title, ...(editable && onUpdateTitle ? styles.clickableText : {}) }}
            onClick={(e) => { e.stopPropagation(); startTitleEdit() }}
          >
            {subgoal.title}
          </span>
        )}

        <span style={styles.facetBadge}>{FACET_LABELS[subgoal.primaryFacet] || subgoal.primaryFacet}</span>
        {subgoal.timeHorizon && <span style={styles.horizon}>{formatTimeHorizon(subgoal.timeHorizon)}</span>}

        {/* Status dropdown */}
        <div style={styles.statusWrapper}>
          <button
            style={{ ...styles.statusChip, background: currentStatus.color + '18', color: currentStatus.color }}
            onClick={(e) => { e.stopPropagation(); setStatusOpen((v) => !v) }}
          >
            {currentStatus.label} ▾
          </button>
          {statusOpen && (
            <div style={styles.statusDropdown}>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  style={styles.statusOption}
                  onClick={(e) => { e.stopPropagation(); onSetStatus?.(opt.value); setStatusOpen(false) }}
                >
                  <span style={{ ...styles.statusDot, background: opt.color, width: '6px', height: '6px' }} />
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {onDelete && (
          <IconButton
            ariaLabel="Delete sub-goal"
            onClick={(e) => { e.stopPropagation(); onDelete() }}
          >
            ×
          </IconButton>
        )}
      </div>

      {/* Render facets — primary first, then others */}
      <div style={styles.facets}>
        {facets.kpi && (
          <KpiFacet kpi={facets.kpi} onUpdateCurrent={editable ? onUpdateKpi : undefined} />
        )}
        {facets.deliverables && (
          <DeliverablesFacet
            deliverables={facets.deliverables}
            onToggle={onToggleDeliverable}
            onAdd={editable ? onAddDeliverable : undefined}
            onRemove={editable ? onRemoveDeliverable : undefined}
          />
        )}
        {facets.milestones && (
          <MilestonesFacet
            milestones={facets.milestones}
            onToggle={onToggleMilestone}
            onAdd={editable ? onAddMilestone : undefined}
            onRemove={editable ? onRemoveMilestone : undefined}
          />
        )}
        {facets.criteria && (
          <CriteriaFacet criteria={facets.criteria} onUpdate={editable ? onUpdateCriteria : undefined} />
        )}
        {(facets.deadline || (editable && onUpdateDeadline)) && (
          <DeadlineFacet deadline={facets.deadline} onUpdate={editable ? onUpdateDeadline : undefined} />
        )}
      </div>

      {/* Notes */}
      {editingNotes ? (
        <textarea
          style={styles.notesInput}
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          onBlur={commitNotes}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitNotes() } }}
          autoFocus
          rows={2}
          placeholder="Add notes..."
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        (subgoal.notes || (editable && onUpdateNotes)) && (
          <p
            style={{ ...styles.notes, ...(editable && onUpdateNotes ? styles.clickableText : {}) }}
            onClick={(e) => { e.stopPropagation(); startNotesEdit() }}
          >
            {subgoal.notes || 'Add notes...'}
          </p>
        )
      )}

      <TrackingSection tracking={tracking} />
    </Card>
  )
}

const styles = {
  header: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  title: { fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', flex: 1 },
  titleInput: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 500,
    padding: '2px 6px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    color: 'var(--text-primary)',
  },
  clickableText: { cursor: 'pointer', borderBottom: '1px dashed var(--border)' },
  facetBadge: {
    fontSize: '9px',
    color: 'var(--text-secondary)',
    background: '#f0f0ee',
    padding: '1px 6px',
    borderRadius: '6px',
    flexShrink: 0,
  },
  horizon: {
    fontSize: '10px',
    color: 'var(--text-secondary)',
    flexShrink: 0,
  },
  statusWrapper: { position: 'relative' },
  statusChip: {
    fontSize: '10px',
    fontWeight: 500,
    padding: '2px 7px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  statusDropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 100,
    minWidth: '120px',
    overflow: 'hidden',
  },
  statusOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    textAlign: 'left',
    padding: '7px 12px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--text-primary)',
  },
  facets: { display: 'flex', flexDirection: 'column', gap: '8px' },
  notes: { fontSize: '11px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, fontStyle: 'italic' },
  notesInput: {
    fontSize: '11px',
    padding: '4px 8px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    fontStyle: 'italic',
    resize: 'vertical',
    lineHeight: 1.5,
  },
}
